/**
 * useGadgetsLibrary — 2026-06-08 Phase 3E wire-up for the canonical
 * Q Manual VG35002 gadgets catalog.
 *
 * Manages the gadgets manifest (59 entries) and the active tab state for
 * the SPA's Phase 3E 3-tab drawer (Common / Security / Exotic). Single
 * manifest (no companion file like Vehicles has with vehicle_modifications),
 * so the hook shape is simpler than useVehiclesLibrary.
 *
 * 3-state union per MEMORY.md rule `feedback_async_then_render_needs_loading_state`:
 * manifest fetch + per-gadget detail fetch each independently tracked.
 * Tab state stays in the hook so toggle-off/toggle-on preserves the
 * user's last selection.
 *
 * Mirrors useEquipmentLibrary.ts + useVehiclesLibrary.ts pattern.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GadgetManifestEntry } from '../services/host/GadgetsServiceModule';
import { GadgetsServiceModule } from '../services/host/GadgetsServiceModule';

export type GadgetsLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export type GadgetsLibraryTab = 'common' | 'security' | 'exotic';

export interface GadgetsLibraryHookResult {
  state: GadgetsLibraryState;
  gadgets: GadgetManifestEntry[];
  error: string | null;
  refresh: () => void;
  activeTab: GadgetsLibraryTab;
  setActiveTab: (tab: GadgetsLibraryTab) => void;
  selectedGadgetId: string | null;
  selectedGadgetData: Record<string, unknown> | null;
  selectedGadgetState: GadgetsLibraryState;
  selectGadget: (gadgetId: string | null) => void;
}

export function useGadgetsLibrary(
  gadgetsService?: GadgetsServiceModule,
): GadgetsLibraryHookResult {
  const resolvedService = useMemo(
    () => gadgetsService ?? new GadgetsServiceModule(),
    [gadgetsService],
  );

  const [state, setState] = useState<GadgetsLibraryState>('idle');
  const [gadgets, setGadgets] = useState<GadgetManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] = useState<GadgetsLibraryTab>('common');

  const [selectedGadgetId, setSelectedGadgetId] = useState<string | null>(null);
  const [selectedGadgetData, setSelectedGadgetData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedGadgetState, setSelectedGadgetState] =
    useState<GadgetsLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectGadget = useCallback((gadgetId: string | null) => {
    setSelectedGadgetId(gadgetId);
    if (gadgetId === null) {
      setSelectedGadgetData(null);
      setSelectedGadgetState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setGadgets(result.gadgets);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setGadgets([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedGadgetId === null) {
      return;
    }
    let cancelled = false;

    setSelectedGadgetState('loading');
    setSelectedGadgetData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedGadgetId);
        if (cancelled) return;
        if (result === null) {
          setSelectedGadgetState('error');
          return;
        }
        setSelectedGadgetData(result);
        setSelectedGadgetState('ready');
      } catch {
        if (cancelled) return;
        setSelectedGadgetState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedGadgetId]);

  return {
    state,
    gadgets,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedGadgetId,
    selectedGadgetData,
    selectedGadgetState,
    selectGadget,
  };
}
